import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, TextField, Button, Box, Paper, Stepper, Step, StepLabel, 
  Grid, Alert, IconButton, InputAdornment, List, ListItem, ListItemText, CircularProgress
} from '@mui/material';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet'; 
import 'leaflet/dist/leaflet.css'; 
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SaveIcon from '@mui/icons-material/Save'; 
import PaymentIcon from '@mui/icons-material/Payment'; 
import { useDropzone } from 'react-dropzone';
import { Link, useNavigate } from 'react-router-dom';
import L from 'leaflet';
import PromoTicket from './components/PromoTicket'; 
import PaymentModal from './PaymentModal'; // <--- IMPORTING YOUR SHARED MODAL
import api from './api';

// --- Fix for default Leaflet marker icons ---
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const steps = ['Location Details', 'Photos & Description', 'Confirm & Pay'];

function LocationMarker({ position, setPosition }) {
  const map = useMapEvents({
    click(e) {
      setPosition(e.latlng);
      map.flyTo(e.latlng, map.getZoom());
    },
  });

  useEffect(() => {
    if (position) {
      map.flyTo(position, 13);
    }
  }, [position, map]);

  return position === null ? null : (
    <Marker position={position}></Marker>
  );
}

function AddDisplay() {
  const navigate = useNavigate();

  // --- AUTH STATE ---
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  // --- FORM STATE ---
  const [activeStep, setActiveStep] = useState(0);
  const [address, setAddress] = useState('');
  const [searchResults, setSearchResults] = useState([]); 
  const [coordinates, setCoordinates] = useState(null); 
  const [description, setDescription] = useState('');
  const [charityUrl, setCharityUrl] = useState('');
  const [photos, setPhotos] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  
  // --- SUBMISSION STATE ---
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [savedDisplayId, setSavedDisplayId] = useState(null); 

  // --- MODAL STATE ---
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [displayForModal, setDisplayForModal] = useState(null);

  // 1. Check Login Status
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
    setCheckingAuth(false);
  }, []);

  // 2. Address Search Function
  const handleSearchAddress = async (query) => {
    setAddress(query);
    if (query.length > 3) {
      try {
        const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&countrycodes=au`);
        const data = await response.json();
        setSearchResults(data);
      } catch (err) {
        console.error("Address search failed", err);
      }
    } else {
      setSearchResults([]);
    }
  };

  const selectAddress = (result) => {
    setAddress(result.display_name);
    setCoordinates({ lat: parseFloat(result.lat), lng: parseFloat(result.lon) });
    setSearchResults([]); 
  };

  // 3. Current Location Function
  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setError("Geolocation is not supported by your browser.");
      return;
    }
    setLoading(true);
    navigator.geolocation.getCurrentPosition(async (position) => {
      const { latitude, longitude } = position.coords;
      setCoordinates({ lat: latitude, lng: longitude });
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`
        );
        const data = await response.json();
        if (data && data.display_name) {
          setAddress(data.display_name);
        } else {
          setAddress(`${latitude}, ${longitude}`);
        }
      } catch (err) {
        setError("Found your location, but couldn't find the address name.");
      } finally {
        setLoading(false);
      }
    }, 
    (err) => {
      setError("Unable to retrieve your location. Please allow location access.");
      setLoading(false);
    });
  };

  // 4. Drag and Drop Logic
  const onDrop = (acceptedFiles) => {
    const newPhotos = [...photos, ...acceptedFiles];
    setPhotos(newPhotos);
    const newPreviews = acceptedFiles.map(file => URL.createObjectURL(file));
    setPreviewUrls([...previewUrls, ...newPreviews]);
  };

  const { getRootProps, getInputProps } = useDropzone({ 
    onDrop, 
    accept: {'image/*': []},
    maxFiles: 5
  });

  const removePhoto = (index) => {
    const newPhotos = [...photos]; newPhotos.splice(index, 1); setPhotos(newPhotos);
    const newPreviews = [...previewUrls]; newPreviews.splice(index, 1); setPreviewUrls(newPreviews);
  };

  // 5. S3 Upload Helper
  const uploadFileToS3 = async (file) => {
    const { data } = await api.post('/photos/upload-request', { fileType: file.type });
    await fetch(data.uploadUrl, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type }
    });
    return data.key;
  };

  // --- CORE SAVE LOGIC ---

  // 6. Save Draft Logic (Used by both buttons)
  const saveToDatabase = async () => {
    // If already saved, return existing ID
    if (savedDisplayId) return savedDisplayId;

    try {
      setLoading(true);
      
      // Upload photos first
      const uploadedKeys = await Promise.all(
        photos.map(photo => uploadFileToS3(photo))
      );

      const payload = {
        address,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        description,
        charity_url: charityUrl,
        photos: uploadedKeys, 
        payment_status: 'pending' // Always start as pending/draft
      };

      const res = await api.post('/displays', payload);
      const newId = res.data.id;
      setSavedDisplayId(newId);
      
      return newId;

    } catch (err) {
      console.error(err);
      throw new Error("Failed to save draft.");
    } finally {
      setLoading(false);
    }
  };

  // 7. Handler: "Save Draft & Pay Later"
  const handleSaveDraft = async () => {
    setError('');
    try {
      await saveToDatabase();
      navigate('/my-submissions');
    } catch (err) {
      setError("Could not save draft. Please try again.");
    }
  };

  // 8. Handler: "Pay & Publish Now" (Opens Modal)
  const handlePayNow = async () => {
    setError('');
    try {
      // Step A: Save Draft First
      const id = await saveToDatabase();
      if (!id) throw new Error("Could not save draft.");

      // Step B: Prepare data for Modal
      setDisplayForModal({
        id: id,
        address: address
      });

      // Step C: Open Modal
      setPaymentModalOpen(true);

    } catch (err) {
      setError("Could not initiate payment. Please try saving as draft first.");
    }
  };

  const handlePaymentSuccess = () => {
    setIsSubmitted(true);
    setPaymentModalOpen(false);
    window.scrollTo(0, 0);
  };

  // --- RENDER ---

  if (checkingAuth) {
    return (
      <Container sx={{ mt: 10, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!isLoggedIn) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <Paper elevation={3} sx={{ p: 5, borderRadius: 2 }}>
          <LockIcon sx={{ fontSize: 60, color: '#d32f2f', mb: 2 }} />
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e' }}>
            Log In Required
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Button variant="contained" size="large" component={Link} to="/login" fullWidth>Log In</Button>
            <Button variant="outlined" size="large" component={Link} to="/register" fullWidth>Create Account</Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mb: 8 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, mt: 4, borderRadius: 2 }}>
        <Typography component="h1" variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e' }}>
          Add Your Christmas Light Display
        </Typography>
        
        {!isSubmitted && <PromoTicket />}

        {!isSubmitted ? (
          <>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* STEP 1: LOCATION */}
            {activeStep === 0 && (
              <Box>
                 <TextField
                   fullWidth
                   label="Type Address..."
                   variant="outlined"
                   value={address}
                   onChange={(e) => handleSearchAddress(e.target.value)}
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                         <IconButton onClick={handleUseCurrentLocation} color="primary">
                           {loading ? <CircularProgress size={24} /> : <MyLocationIcon />}
                         </IconButton>
                       </InputAdornment>
                     ),
                   }}
                   sx={{ mb: 1 }}
                 />
                 
                 {searchResults.length > 0 && (
                   <List sx={{ border: '1px solid #ddd', borderRadius: 1, maxHeight: '200px', overflowY: 'auto', mb: 2 }}>
                     {searchResults.map((result) => (
                       <ListItem button key={result.place_id} onClick={() => selectAddress(result)}>
                         <ListItemText primary={result.display_name} />
                       </ListItem>
                     ))}
                   </List>
                 )}
                 
                 <Box sx={{ height: '300px', width: '100%', borderRadius: 2, overflow: 'hidden', mt: 2, border: '1px solid #ccc' }}>
                   <MapContainer center={[-25.2744, 133.7751]} zoom={4} style={{ height: '100%', width: '100%' }}>
                     <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
                     <LocationMarker position={coordinates} setPosition={setCoordinates} />
                   </MapContainer>
                 </Box>
              </Box>
            )}

            {/* STEP 2: PHOTOS & DETAILS */}
            {activeStep === 1 && (
              <Box>
                <Typography variant="h6" gutterBottom>Description (Optional)</Typography>
                <TextField 
                  fullWidth multiline rows={3} label="Tell us about your display..."
                  value={description} onChange={(e) => setDescription(e.target.value)} sx={{ mb: 3 }}
                />

                <Typography variant="h6" gutterBottom>Charity Fundraiser URL (Optional)</Typography>
                <TextField 
                  fullWidth placeholder="https://gofundme.com/..." label="Link to your fundraiser"
                  value={charityUrl} onChange={(e) => setCharityUrl(e.target.value)} sx={{ mb: 3 }}
                />

                <Typography variant="h6" gutterBottom>Upload Photos</Typography>
                <Box 
                  {...getRootProps()} 
                  sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 3, textAlign: 'center', cursor: 'pointer', bgcolor: '#fafafa' }}
                >
                  <input {...getInputProps()} />
                  <CloudUploadIcon sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography>Drag & drop photos here, or click to select</Typography>
                </Box>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                  {previewUrls.map((url, index) => (
                    <Grid item xs={6} sm={4} md={3} key={index} sx={{ position: 'relative' }}>
                      <Box component="img" src={url} sx={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: 1 }} />
                      <IconButton 
                        size="small" onClick={() => removePhoto(index)}
                        sx={{ position: 'absolute', top: 5, right: 5, bgcolor: 'rgba(255,255,255,0.8)' }}
                      >
                        <DeleteIcon color="error" fontSize="small" />
                      </IconButton>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* STEP 3: ACTIONS */}
            {activeStep === 2 && (
              <Box sx={{ textAlign: 'center' }}>
                <Alert severity="info" icon={<CheckCircleIcon />} sx={{ mb: 4 }}>
                   {address || "Coordinates Selected"}
                </Alert>
                
                <Typography variant="h6" gutterBottom>Listing Fee: $2.00 AUD</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  (Discounts can be applied in the next step)
                </Typography>

                {/* Option 1: Pay Now (Opens Modal) */}
                <Button 
                  fullWidth 
                  variant="contained" 
                  size="large" 
                  startIcon={<PaymentIcon />}
                  onClick={handlePayNow}
                  disabled={loading}
                  sx={{ mb: 2, py: 1.5, fontSize: '1.1rem' }}
                >
                  Pay & Publish Now
                </Button>

                <Typography variant="body2" sx={{ my: 1 }}>- OR -</Typography>

                {/* Option 2: Save Draft */}
                <Button 
                  fullWidth 
                  variant="outlined" 
                  size="large" 
                  color="secondary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveDraft}
                  disabled={loading}
                >
                  Save Display & Pay Later
                </Button>
              </Box>
            )}

            {/* NAV BUTTONS */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                disabled={activeStep === 0}
                onClick={() => setActiveStep(prev => prev - 1)}
                variant="outlined"
              >
                Back
              </Button>
              {activeStep < steps.length - 1 && (
                <Button
                  variant="contained"
                  onClick={() => { 
                      if(activeStep===0 && !coordinates) {
                          setError("Please select a valid address or click on the map.");
                          return;
                      }
                      setError(''); 
                      setActiveStep(prev => prev + 1); 
                  }}
                >
                  Next
                </Button>
              )}
            </Box>
          </>
        ) : (
          /* SUCCESS STATE */
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h4" gutterBottom>Display Submitted!</Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Thank you! Your display is now live on the map.
            </Typography>
            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
               <Button variant="contained" component={Link} to="/">View Map</Button>
               <Button variant="outlined" component={Link} to="/my-submissions">Manage My Displays</Button>
            </Box>
          </Box>
        )}

        {/* --- REUSED PAYMENT MODAL --- */}
        {displayForModal && (
          <PaymentModal
            open={paymentModalOpen}
            onClose={() => setPaymentModalOpen(false)}
            display={displayForModal}
            onPaymentSuccess={handlePaymentSuccess}
          />
        )}

      </Paper>
    </Container>
  );
}

export default AddDisplay;